import { Flex, FlexProps } from '@chakra-ui/react';
import { FEATURE_FLAGS } from '~/constants';
import { CONTENT_SEARCH_PARAMS } from '~/hooks/useContentSearch';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';

import {
  CONTENT_SEARCH_COMPONENTS,
  CONTENT_SEARCH_DEFAULT_ENABLED_COMPONENTS,
  CONTENT_SEARCH_DEFAULT_ORDER_COMPONENTS,
  CONTENT_SEARCH_DEFAULT_TRACK_ID,
  CONTENT_SEARCH_DISABLED_COMPONENTS,
} from './ContentSearchFilter.constants';
import {
  ContentSearchDisabledComponentType,
  ContentSearchEnabledComponentType,
  ContentSearchOrderComponentType,
  ContentSearchTrackIdComponentType,
  IContentSearchQuery,
} from './ContentSearchFilter.types';

export interface IContentSearchFilterParams
  extends Omit<FlexProps, 'onChange'> {
  enabled?: ContentSearchEnabledComponentType;
  filters: IContentSearchQuery;
  onChange: (params: IContentSearchQuery) => void;
  order?: ContentSearchOrderComponentType;
  trackIds?: ContentSearchTrackIdComponentType;
  disabledComponents?: ContentSearchDisabledComponentType;
  /**
   * Currently, we add new filters button to the end of the list. This is behind feature flag.
   *
   * A follow up ticket implements new subnavbar layout which will have correct position for the button.
   * The above ContentSearchFilter component will be deprecated in favour of new SubnavBar as the
   * current state of featureset doesn't require the extreme configurability.
   */
  temporaryLastChildElement?: React.ReactNode;
}

const HIDE_WHEN_GLOBAL_CONTENT_FILTER_ENABLED = [
  CONTENT_SEARCH_PARAMS.creator,
  CONTENT_SEARCH_PARAMS.platform,
  CONTENT_SEARCH_PARAMS.ads,
];

export const ContentSearchFilter: React.FC<IContentSearchFilterParams> = ({
  enabled = {},
  filters,
  onChange,
  order = {},
  trackIds = {},
  disabledComponents = {},
  temporaryLastChildElement,
  ...flexProps
}) => {
  const componentOrder = {
    ...CONTENT_SEARCH_DEFAULT_ORDER_COMPONENTS,
    ...order,
  };
  const componentTrackId = {
    ...CONTENT_SEARCH_DEFAULT_TRACK_ID,
    ...trackIds,
  };

  const componentDisabled = {
    CONTENT_SEARCH_DISABLED_COMPONENTS,
    ...disabledComponents,
  };

  const handleChange = (updatedFilters: Partial<IContentSearchQuery>) => {
    onChange({
      ...filters,
      ...updatedFilters,
      [CONTENT_SEARCH_PARAMS.page]: '1',
    });
  };

  const { isEnabled: isGlobalContentFiltersEnabled } = useFeature(
    FEATURE_FLAGS.foamContentGlobalFilters,
  );

  return (
    <Flex
      flexWrap="wrap"
      alignItems="center"
      justifyContent={{ base: 'flex-start', md: 'flex-end' }}
      gap={2}
      marginBottom="spacing-300"
      data-testid="content-search-filter"
      {...flexProps}
    >
      {Object.entries({
        ...CONTENT_SEARCH_DEFAULT_ENABLED_COMPONENTS,
        ...enabled,
      })
        .filter(([, value]) => value)
        .filter(([key]) => {
          if (!isGlobalContentFiltersEnabled) {
            return true;
          }
          return !HIDE_WHEN_GLOBAL_CONTENT_FILTER_ENABLED.includes(
            key as keyof ContentSearchEnabledComponentType,
          );
        })
        .map(([key]) => {
          const componentKey = key as keyof ContentSearchEnabledComponentType;
          const Component = CONTENT_SEARCH_COMPONENTS[componentKey];
          const trackId = componentTrackId[componentKey];
          const isDisabled = componentDisabled[componentKey];
          return (
            <Component
              filters={filters}
              key={key}
              onChange={handleChange}
              order={componentOrder[componentKey]}
              trackId={trackId}
              isDisabled={isDisabled}
            />
          );
        })}
      {temporaryLastChildElement}
    </Flex>
  );
};
